import { defineAsyncComponent } from "vue";
import type { AsyncComponentLoader, Component } from "vue";

import ContentSkeleton from "@/components/layout/ContentSkeleton.vue";
import LazyViewError from "@/components/layout/LazyViewError.vue";

/** A chunk that arrives inside this window (the usual service-worker cache hit) never flashes the skeleton. */
export const LAZY_VIEW_DELAY_MS = 200;

/** After this the error view is shown; a chunk that still arrives later replaces it. */
export const LAZY_VIEW_TIMEOUT_MS = 15_000;

/** One silent retry covers a dropped request; a stale chunk from an old deploy fails again and needs the reload. */
export const LAZY_VIEW_RETRIES = 1;

/** What a caller may change: a whole-screen view (App.vue's member layout) needs its own placeholder, shown at once. */
export interface LazyViewOptions {
    loadingComponent?: Component;
    delay?: number;
}

/**
 * defineAsyncComponent for a whole view: the product-key registrations in main.ts that CachedComponentWrapper and
 * ProductWorkspace render with `<component :is>`. A bare defineAsyncComponent( loader ) rendered nothing while its
 * chunk downloaded, so the content area stayed blank after the route had already changed, and nothing at all if the
 * download failed.
 *
 * Not suspensible: none of these views sit under a Suspense that should wait for them, and a suspensible async
 * component ignores its own loading and error components.
 *
 * Vue keeps the final rejected load, so once a chunk has failed after the retry, every later mount of that view in
 * the same tab shows LazyViewError straight away without trying again; its Reload page button is the way out.
 */
export function lazyView< T extends Component >( loader: AsyncComponentLoader< T >, options: LazyViewOptions = {} ): T {
    return defineAsyncComponent( {
        loader,
        loadingComponent: options.loadingComponent ?? ContentSkeleton,
        errorComponent: LazyViewError,
        delay: options.delay ?? LAZY_VIEW_DELAY_MS,
        timeout: LAZY_VIEW_TIMEOUT_MS,
        suspensible: false,
        onError( _error, retry, fail, attempts ) {
            if ( attempts <= LAZY_VIEW_RETRIES ) {
                retry();
            } else {
                fail();
            }
        }
    } );
}
